import React from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import IconButton from 'components/IconButton';
import { AppBannerContext } from 'components/AppBanner';
import Tooltip, { TooltipProps } from 'components/Tooltip';
import ChevronRightIcon from '@nokia-csf-uxr/ccfk-assets/ChevronRightIcon';
import ChevronLeftIcon from '@nokia-csf-uxr/ccfk-assets/ChevronLeftIcon';
import Styled from './styles';

const DEFAULT_TOOLTIP = (expanded: boolean) => (expanded ? 'Collapse drawer' : 'Expand drawer');

export interface AppBannerDrawerProps {
  /** Place AppBanner drawer content here, for example UserAccountSummary or AppMenu. */
  children: string | React.ReactNode;
  /** Pass tooltip props here */
  // TODO: [ASK MOE] why any is needed here.
  tooltipProps?: Partial<TooltipProps> | any;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

export interface ThemeShape {
  APP_BANNER_INTERACTIVE_CONTENT: Record<string, unknown>
}

// TODO: not sure how to resolve this elegantly, export interface seems overkill for adhoc objects
export interface ButtonTheme {
  content: unknown,
  container: unknown,
  hovered: {
    container: unknown,
    content: unknown
  },
  focused: {
    content: unknown,
    border: unknown
  }
}

/** AppBannerDrawer component, allows collapse/expand of multiple AppBanner components when content doesn't have enough space in smaller views. */
const AppBannerDrawer:React.FC<AppBannerDrawerProps> = React.forwardRef<HTMLElement, AppBannerDrawerProps>((props: AppBannerDrawerProps, ref) =>
  getContext(({ RTL, theme }: { theme?: ThemeShape, RTL: boolean }) => {
    const {
      children,
      theme: propTheme,
      tooltipProps,
      ...otherProps
    } = props;
    const containerRef = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, containerRef);
    const { mobileView, expanded, setExpanded }
    : any = React.useContext(AppBannerContext);
    const resolvedTheme: any = _merge({}, theme.APP_BANNER_INTERACTIVE_CONTENT, propTheme);
    const buttonTheme: ButtonTheme = {
      content: resolvedTheme.content,
      container: resolvedTheme.container,
      hovered: {
        container: resolvedTheme.hovered.background,
        content: resolvedTheme.hovered.content
      },
      focused: {
        content: resolvedTheme.focused.content,
        border: resolvedTheme.focused.border
      }
    };
    return (
      <>
        <Styled.AppBannerDrawer
          $rtl={RTL}
          ref={combinedRef}
          $theme={resolvedTheme}
          $expanded={expanded}
          mobileView={mobileView}
          {...otherProps}
        >
          {children}
        </Styled.AppBannerDrawer>
        {mobileView &&
          <Tooltip
            closeOnOutOfBoundaries={false}
            placement="bottom"
            trigger={['hover', 'focus']}
            modifiers={{ offset: { offset: '0, 10' } }}
            tooltip={DEFAULT_TOOLTIP(expanded)}
            {...tooltipProps}
          >
            <IconButton
              theme={buttonTheme}
              aria-label="settings"
              onClick={() => {
                setExpanded(!expanded);
              }}
            >
              {expanded ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        }
      </>
    );
  }));

AppBannerDrawer.defaultProps = {
  theme: {},
  tooltipProps: {}
};

AppBannerDrawer.displayName = 'AppBannerDrawer';

export default AppBannerDrawer;
